import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Icon, Input, Text } from 'view/atoms';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { inviteGuest, type ActiveGuest } from 'services/aPlayer';
import { useAppDispatch } from 'redux/hooks';
import { showErrorAlert, showSimpleSuccessAlert } from 'redux/utils';
import { setAlert, showAlert } from 'redux/modules/Alert/alert';
import { AlertType } from 'interfaces';
import { capitalize } from 'utils';
import { users, message } from 'static/icons/resizable';
import { IconPositionInInput } from 'view/atoms/Input/Input.model';
import * as S from '../../InviteGuestModal.styles';
import { schema } from './InviteGuestForm.schema';

type FormFields = {
  firstName: string;
  lastName: string;
  email: string;
};

type Props = {
  onSuccess: () => void;
  onCancel: () => void;
  history?: ActiveGuest[];
};

export const InviteGuestForm = ({ onSuccess, onCancel, history }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const {
    register,
    formState: { errors, isValid: isSubmitEnabled, isSubmitting },
    handleSubmit,
    setValue,
    watch,
  } = useForm<FormFields>({
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: FormFields) => {
    const emailAlreadyInvited = history?.some(
      (guest) => guest.email.toLowerCase() === data.email.toLowerCase(),
    );

    if (emailAlreadyInvited) {
      dispatch(
        setAlert({
          type: AlertType.error,
          title: t('aPlayerPage.inviteGuest.emailAlreadyInvitedError'),
          bodyText: '',
        }),
      );
      dispatch(showAlert());
      return;
    }

    try {
      await inviteGuest(data);
      onSuccess();
      showSimpleSuccessAlert(
        dispatch,
        t('aPlayerPage.inviteGuest.successTitle'),
        t('aPlayerPage.inviteGuest.successBody'),
      );
    } catch (err) {
      showErrorAlert(dispatch, err, t('aPlayerPage.inviteGuest.errorTitle'));
    }
  };

  return (
    <S.ModalFormWrapper>
      <S.ContentWrapper>
        <S.HeaderWrapper>
          <Text kind='h2'>{t('aPlayerPage.inviteGuest.modalTitle')}</Text>
          <Text kind='body'>
            {t('aPlayerPage.inviteGuest.modalDescription')}
          </Text>
        </S.HeaderWrapper>

        <Input
          type='text'
          placeholder={t('aPlayerPage.inviteGuest.namePlaceholder')}
          icon={<Icon src={users} alt='name icon' />}
          iconPosition={IconPositionInInput.left}
          {...register('firstName', {
            onChange: (e) => setValue('firstName', capitalize(e.target.value)),
            onBlur: (e) => setValue('firstName', capitalize(e.target.value)),
          })}
          error={errors.firstName?.message && t(errors.firstName.message)}
          value={watch('firstName')}
        />

        <Input
          type='text'
          placeholder={t('aPlayerPage.inviteGuest.surnamePlaceholder')}
          icon={<Icon src={users} alt='surname icon' />}
          iconPosition={IconPositionInInput.left}
          {...register('lastName', {
            onChange: (e) => setValue('lastName', capitalize(e.target.value)),
            onBlur: (e) => setValue('lastName', capitalize(e.target.value)),
          })}
          error={errors.lastName?.message && t(errors.lastName.message)}
          value={watch('lastName')}
        />

        <Input
          type='text'
          placeholder={t('aPlayerPage.inviteGuest.emailPlaceholder')}
          icon={<Icon src={message} alt='email icon' />}
          iconPosition={IconPositionInInput.left}
          {...register('email')}
          error={errors.email?.message && t(errors.email.message)}
          value={watch('email')}
        />
      </S.ContentWrapper>

      <S.ButtonsWrapper>
        <Button
          kind='primary'
          disabled={!isSubmitEnabled || isSubmitting}
          onClick={handleSubmit(onSubmit)}
        >
          <Text kind='button'>{t('aPlayerPage.inviteGuest.send')}</Text>
        </Button>

        <Button kind='secondary' onClick={onCancel}>
          <Text kind='button'>{t('aPlayerPage.inviteGuest.cancel')}</Text>
        </Button>
      </S.ButtonsWrapper>
    </S.ModalFormWrapper>
  );
};
